import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDecision } from '@optimizely/react-sdk';
import { OPTIMIZELY_FLAGS } from 'config/optimizely';
import { UnitReservationFormSelector } from 'store/selectors/bookings';
import { getLocalPhoneNumberByCityState } from 'store/pageMeta/slice';
import {
	email as emailValidator,
	phone as phoneValidator,
} from 'utils/form/validation';
import { Form, FormItem, FormGroup, TextInput } from 'components/core/Form';
import { ReduxCAPTCHA } from 'components/form';
import { Spinner } from 'components/core/Spinner';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import { Link } from 'components/core/Link';
import { Paragraph, Text } from 'components/core/Typography';

import warningIcon from 'assets/images/icons/warning-icon.svg';

import UnitReservationFeeDropDown from 'components/fees/UnitReservationFeeDropDown';
import GiftCardModalLink from 'components/giftcard/GiftCardModalLink/GiftCardModalLink';
import { reservationFormHandler } from 'utils/reservationFormHandler';
import UnitDropdownDatepicker from '../UnitDropDownDatepicker/UnitDropdownDatepicker';
import AlternateNamesContainer from './AlternateNamesContainer';

import './unitReservationForm.scss';
import { trackCTAClick } from '../../core/library/tracking-utils/tracking/clickTrackingUtils';
import { GiftCardBanner } from 'components/Banners/GiftCardBanner';

const fetchLocalPhoneNumber = async (
	city,
	state,
	getLocalPhoneNumberByCityState,
	setLocalPhone,
) => {
	try {
		const phone = await getLocalPhoneNumberByCityState(city, state);
		const { number, unformattedNumber } = phone?.payload || {};
		try {
			setLocalPhone({
				number: number || '1-800-555-5555',
				unformattedNumber: unformattedNumber || '18005555555',
			});
		} catch (e) {
			// Ignore errors in test environments where DOM might not be available
		}
	} catch (error) {
		try {
			setLocalPhone({
				number: '1-800-555-5555',
				unformattedNumber: '18005555555',
			});
		} catch (e) {
			// Ignore errors in test environments where DOM might not be available
		}
		console.error(error);
	}
};

// Helper to adapt react-hook-form's Controller render props to legacy input/meta props
function controllerToFieldProps(field, fieldState, { onSave, onClearError } = {}) {
	return {
		input: {
			name: field.name,
			value: field.value ?? '',
			onChange: (...args) => {
				field.onChange(...args);
				if (onClearError) onClearError();
				if (onSave) onSave();
			},
			onBlur: (...args) => {
				field.onBlur(...args);
				if (onSave) onSave();
			},
		},
		meta: {
			touched: fieldState.isTouched || !!fieldState.error,
			error: fieldState.error?.message,
			valid: !fieldState.error,
			invalid: !!fieldState.error,
		},
	};
}

export const enhance = (Component) => Component;

export const UnitReservationForm = forwardRef(function UnitReservationForm(
	{
		unit,
		today = new Date(),
		onSubmit: submitBooking,
		facility,
		savedFormValues,
		onFormValuesChange,
	},
	ref,
) {
	const dispatch = useDispatch();
	const { recaptchaSiteKey, closures } = useSelector((state) =>
		UnitReservationFormSelector(state, { facility }),
	);
	const boundGetLocalPhoneNumberByCityState = useCallback(
		(city, state) => dispatch(getLocalPhoneNumberByCityState(city, state)),
		[dispatch],
	);
	const [botErrors, setBotErrors] = useState(false);
	const [lastSubmission, setLastSubmission] = useState(null);
	const [localPhone, setLocalPhone] = useState(null);
	const [formError, setFormError] = useState(null);

	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		formState: { isSubmitting, isValid },
	} = useForm({
		mode: 'onChange',
		defaultValues: {
			firstName: savedFormValues?.firstName || '',
			lastName: savedFormValues?.lastName || '',
			email: savedFormValues?.email || '',
			phone: savedFormValues?.phone || '',
			moveInDate: savedFormValues?.moveInDate || '',
			alternateNames: [],
			recaptchaToken: '',
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'alternateNames',
	});

	const saveFormValues = useCallback(() => {
		if (onFormValuesChange) {
			const values = getValues();
			onFormValuesChange({
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
				phone: values.phone,
				moveInDate: values.moveInDate,
			});
		}
	}, [onFormValuesChange, getValues]);

	const onSubmit = useCallback(
		({ alternateNames = [], ...formData }) => {
			const {
				location: { city, state },
			} = facility;
			const noChangeSinceLastSubmission =
				lastSubmission &&
				JSON.stringify(lastSubmission) === JSON.stringify(formData);
			if (noChangeSinceLastSubmission) {
				if (!localPhone) {
					fetchLocalPhoneNumber(
						city,
						state,
						boundGetLocalPhoneNumberByCityState,
						setLocalPhone,
					);
				}
				setFormError('ReSubmissionError');
				return;
			} else {
				setLastSubmission(formData);
				setFormError(null);
				const bookingDetails = {
					...formData,
					email: formData.email?.toLowerCase(),
					facilityId: facility.id,
					unitId: unit.id,
					alternateFirstNames: alternateNames.map(
						(name) => name.firstName,
					),
					alternateLastNames: alternateNames.map(
						(name) => name.lastName,
					),
				};

				let click_url = unit?.url || null;

				trackCTAClick(
					'complete-reservation',
					'Complete Reservation Button Clicked',
					'Complete Reservation',
					click_url,
				);

				return submitBooking(bookingDetails)
					.then((response) =>
						reservationFormHandler(response, {
							botErrors,
							setBotErrors,
						}),
					)
					.catch((error) => {
						fetchLocalPhoneNumber(
							city,
							state,
							boundGetLocalPhoneNumberByCityState,
							setLocalPhone,
						);
						setFormError(error.errors?._error || error.message);
					});
			}
		},
		[
			lastSubmission,
			localPhone,
			facility,
			unit,
			submitBooking,
			botErrors,
			boundGetLocalPhoneNumberByCityState,
		],
	);

	// Expose setValue to parent via ref so FacilityPage can reset moveInDate
	useImperativeHandle(
		ref,
		() => ({
			setValue,
			submit: () => handleSubmit(onSubmit)(),
		}),
		[setValue, handleSubmit, onSubmit],
	);

	// A/A test: bucket users for reporting verification (no UI change)
	useDecision(OPTIMIZELY_FLAGS.UNIT_RESERVATION_AA_TEST.key);

	const handleSubmitButton = (e) => {
		e.preventDefault();
		handleSubmit(onSubmit)();
	};

	const {
		showFeeWarning,
		url: { city },
	} = facility;

	return (
		<div className="unit-reservation-form">
			<Form
				onSubmit={handleSubmit(onSubmit)}
				invalid={!isValid}
				successLabel={`success-web-${unit.unitType}-${unit.width}x${unit.length}`}
				failureLabel={`fail-web-${unit.unitType}-${unit.width}x${unit.length}`}
				id="unit-reservation-form"
			>
				<FormGroup>
					<FormItem>
						<Controller
							name="firstName"
							control={control}
							rules={{ required: 'Please provide your first name' }}
							render={({ field, fieldState }) => (
								<TextInput
									{...controllerToFieldProps(field, fieldState, { onSave: saveFormValues, onClearError: () => setFormError(null) })}
									label="First name"
									errorMessage="Please provide your first name"
								/>
							)}
						/>
					</FormItem>
					<FormItem>
						<Controller
							name="lastName"
							control={control}
							rules={{ required: 'Please provide your last name' }}
							render={({ field, fieldState }) => (
								<TextInput
									{...controllerToFieldProps(field, fieldState, { onSave: saveFormValues, onClearError: () => setFormError(null) })}
									label="Last name"
									errorMessage="Please provide your last name"
								/>
							)}
						/>
					</FormItem>
				</FormGroup>
				<FormGroup>
					<FormItem>
						<Controller
							name="email"
							control={control}
							rules={{
								required: 'Please provide a valid email address',
								validate: (value) => {
									const result = emailValidator(value);
									return result || true;
								},
							}}
							render={({ field, fieldState }) => (
								<TextInput
									{...controllerToFieldProps(field, fieldState, { onSave: saveFormValues, onClearError: () => setFormError(null) })}
									label="Email address"
									type="email"
									errorMessage="Please provide a valid email address"
								/>
							)}
						/>
					</FormItem>
					<FormItem>
						<Controller
							name="phone"
							control={control}
							rules={{
								required: 'Please provide a valid phone number',
								validate: (value) => {
									const result = phoneValidator(value);
									return result || true;
								},
							}}
							render={({ field, fieldState }) => (
								<TextInput
									{...controllerToFieldProps(field, fieldState, { onSave: saveFormValues, onClearError: () => setFormError(null) })}
									label="Phone number"
									errorMessage="Please provide a valid phone number"
								/>
							)}
						/>
					</FormItem>
				</FormGroup>
				<FormGroup className="datepicker-credit-card-row">
					<FormItem className="move-in-date">
						<Controller
							name="moveInDate"
							control={control}
							rules={{ required: 'Please provide a move-in date' }}
							render={({ field, fieldState }) => (
								<UnitDropdownDatepicker
									{...controllerToFieldProps(field, fieldState, { onSave: saveFormValues, onClearError: () => setFormError(null) })}
									label="Move-in date"
									reservationWindow={unit.reservationWindow}
									today={today}
									closures={closures}
									errorMessage="Please provide a move-in date"
								/>
							)}
						/>
						<Text>
							If you're unsure of your move-in date, select an
							estimated date. This can be changed later.
						</Text>
					</FormItem>
				</FormGroup>
				<AlternateNamesContainer
					fields={fields}
					append={append}
					remove={remove}
					control={control}
				/>

				{showFeeWarning && !!facility?.fees?.totalFees?.value && (
					<UnitReservationFeeDropDown
						fees={facility?.fees}
						price={unit.price}
					/>
				)}

				{!!botErrors && (
					<FormGroup>
						<Controller
							name="recaptchaToken"
							control={control}
							render={({ field }) => (
								<ReduxCAPTCHA
									input={{
										name: field.name,
										value: field.value,
										onChange: (val) => {
											field.onChange(val);
											setFormError(null);
										},
										onBlur: field.onBlur,
									}}
									sitekey={recaptchaSiteKey}
								/>
							)}
						/>
					</FormGroup>
				)}
				{!botErrors && (
					<input type="hidden" name="recaptchaToken" />
				)}

				{!!formError && !isSubmitting && !botErrors && (
					<div className="main-alert-container">
						<Image
							src={warningIcon}
							alt="warning-icon"
						/>
						<div>
							<Paragraph last>
								{'Your reservation had an error. '}
							</Paragraph>
							<Paragraph last>
								{
									'Please check the information you provided and make a change to resubmit your reservation.'
								}
								{formError !== 'ReSubmissionError' && (
									<>
										<br /> &nbsp;&#8226; {formError}{' '}
									</>
								)}
							</Paragraph>
						</div>
					</div>
				)}
				<FormGroup>
					<FormItem fill>
						<Button
							type="button"
							onClick={handleSubmitButton}
							disabled={isSubmitting || !!formError}
							className="submit-button"
							pink
						>
							{'Complete reservation'}
						</Button>
					</FormItem>
				</FormGroup>
				{!!formError && !isSubmitting && !botErrors && (
					<div className="main-alert-container">
						<Paragraph last>
							If you continue to experience errors with this
							reservation, you may need to come back and try again
							at a later time or &nbsp;
							<Link
								href={city}
								rel="noopener noreferrer"
								hideBorder
							>
								try exploring other storage options in the same
								area if you are looking to reserve storage right
								now
							</Link>{' '}
							<span>
								or call{' '}
								<a
									href={`tel:${localPhone?.unformattedNumber}`}
								>
									{localPhone?.number}
								</a>{' '}
								to get help in finding the best storage option
								for you.
							</span>
						</Paragraph>
					</div>
				)}
				{facility.giftCardOffer?.giftCardValue && (
					<GiftCardBanner
						className="reservation-gift-card-banner"
						content={<GiftCardModalLink />}
					/>
				)}

				<FormGroup key="tos">
					<Paragraph
						className="tos-text"
						color="gray"
					>
						No obligation to rent. By clicking to reserve this unit,
						you agree to our
						<Link
							href="/legal/consumer"
							target="_blank"
							rel="noopener noreferrer"
						>
							Terms of Service
						</Link>{' '}
						and
						<Link
							href="/legal/privacy"
							target="_blank"
							rel="noopener noreferrer"
						>
							Privacy Policy
						</Link>
						.
					</Paragraph>
				</FormGroup>
			</Form>
			{!!isSubmitting && (
				<div className="spinner-overlay">
					<Spinner show />
				</div>
			)}
		</div>
	);
});

UnitReservationForm.propTypes = {
	unit: PropTypes.object.isRequired,
	today: PropTypes.instanceOf(Date),
	facility: PropTypes.object.isRequired,
	onSubmit: PropTypes.func,
	savedFormValues: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
		phone: PropTypes.string,
		moveInDate: PropTypes.string,
	}),
	onFormValuesChange: PropTypes.func,
	isScarce: PropTypes.bool,
	bookingType: PropTypes.string,
};

export default UnitReservationForm;
